import tw from 'twin.macro';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { AlertTriangle, SquareArrowOutUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { RerecordingStatusBadge } from '@/components/RerecordingStatusBadge';
import { BackofficeSession, RerecordingStatus, SessionStatus } from '@/libs/graphql';
import getStatusStyle from '@/utils/getStatusStyle';

import { getSessionStatusText } from '../../../utils/getSessionStatusText';

const columnHelper = createColumnHelper<Partial<BackofficeSession>>();

const SessionDataTable = ({
  sessions,
  hideRerecordingStatus,
}: {
  sessions?: Partial<BackofficeSession>[];
  hideRerecordingStatus?: boolean;
}) => {
  const { t, i18n } = useTranslation();

  const columns = [
    columnHelper.accessor('refId', {
      header: () => (
        <span>
          {i18n.exists('intervieweeProfiles.refId.tableLabel')
            ? t('intervieweeProfiles.refId.tableLabel')
            : t('intervieweeProfiles.refId.label')}
        </span>
      ),
      cell: (props) => <p tw="text-center">{props.getValue() ?? '-'}</p>,
      size: 86,
    }),
    columnHelper.accessor('intervieweeName', {
      header: () => (
        <span>
          {i18n.exists('intervieweeProfiles.intervieweeName.tableLabel')
            ? t('intervieweeProfiles.intervieweeName.tableLabel')
            : t('intervieweeProfiles.intervieweeName.label')}
        </span>
      ),
      cell: (props) => props.getValue(),
      size: 150,
    }),
    columnHelper.accessor('intervieweeEmail', {
      header: () => (
        <span>
          {i18n.exists('intervieweeProfiles.intervieweeEmail.tableLabel')
            ? t('intervieweeProfiles.intervieweeEmail.tableLabel')
            : t('intervieweeProfiles.intervieweeEmail.label')}
        </span>
      ),
      cell: (props) => props.getValue(),
      size: 150,
    }),
    columnHelper.accessor('position', {
      header: () => (
        <span>
          {i18n.exists('intervieweeProfiles.position.tableLabel')
            ? t('intervieweeProfiles.position.tableLabel')
            : t('intervieweeProfiles.position.label')}
        </span>
      ),
      cell: (props) => props.getValue(),
      size: 150,
    }),
    columnHelper.accessor('questionTemplateName', {
      header: () => <span>ชุดคำถาม</span>,
      cell: (props) => props.getValue(),
      size: 150,
    }),
    columnHelper.accessor('status', {
      header: () => <span>สถานะ</span>,
      cell: ({ row }) => {
        return (
          <div tw="flex flex-col items-center justify-center gap-1.5">
            {/* Session Status */}
            <div tw="flex items-center gap-2">
              <span
                css={[
                  tw`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium`,
                  getStatusStyle(
                    getSessionStatusText(
                      row.original.status as SessionStatus,
                      row.original.availableUntil,
                      row.original.expiredAt,
                    ),
                  ),
                ]}
              >
                {getSessionStatusText(
                  row.original.status as SessionStatus,
                  row.original.availableUntil,
                  row.original.expiredAt,
                )}
              </span>
              {row.original.hasCorruptedVideo && (
                <div title={t('sessionViewer.video.corruptedTooltip')}>
                  <AlertTriangle tw="h-4 w-4 text-red-500" />
                </div>
              )}
            </div>

            {/* Re-recording Status */}
            {!hideRerecordingStatus &&
              row.original.rerecordingStatus &&
              row.original.rerecordingStatus !== RerecordingStatus.None && (
                <div tw="flex items-center gap-2">
                  <RerecordingStatusBadge status={row.original.rerecordingStatus} size="sm" />
                </div>
              )}
          </div>
        );
      },
      size: 220,
    }),
    columnHelper.accessor('completedAt', {
      header: () => <span>เวลาที่สัมภาษณ์เสร็จ</span>,
      cell: (props) => (
        <p tw="text-center">
          {props.getValue() ? dayjs(props.getValue()).format('DD/MM/YYYY HH:mm') : '-'}
        </p>
      ),
      size: 200,
    }),
    columnHelper.accessor((row) => row?.aiMetadata?.highlightCount, {
      id: 'highlightCount',
      header: 'Highlights',
      cell: (props) => <p tw="text-center text-blue-700 font-bold">{props.getValue() ?? '-'}</p>,
      size: 100,
    }),
    columnHelper.accessor('id', {
      header: () => <span>ดูข้อมูล</span>,
      cell: (props) => (
        // not using navigate because we want it to open in new tab
        <SquareArrowOutUpRight
          role="button"
          tw="h-5 w-5 text-center m-auto text-amber-400"
          onClick={() =>
            window.open(
              `/backoffice/interviewee/${props.getValue()}`,
              '_blank',
              'noopener, noreferrer',
            )
          }
        />
      ),
      size: 50,
    }),
  ];

  const table = useReactTable({
    data: sessions as Partial<BackofficeSession>[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 100,
      minSize: 100,
      maxSize: 180,
    },
  });

  return (
    <div tw="overflow-auto rounded-lg border-2 bg-white">
      <table tw="table-auto w-full">
        <thead tw="border-b-2 text-center">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} style={{ height: '45px' }}>
              {headerGroup.headers.map((header) => (
                <th tw="py-4 px-4" key={header.id} style={{ width: `${header.getSize()}px` }}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} tw="text-center py-8 text-gray-400">
                ไม่มีข้อมูล
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row, index) => {
              return (
                <tr
                  key={row.id}
                  css={[
                    tw`border-b text-sm`,
                    sessions && index === sessions?.length - 1 && tw`border-none`,
                  ]}
                  style={{ height: '45px' }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const isStatus = cell.column.id === 'status';

                    return (
                      <td key={cell.id} css={[tw`py-1 px-2`, isStatus && tw`text-center`]}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SessionDataTable;
